import Stack from '@mui/material/Stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import { Lock } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useLangFile } from 'js/context/LanguageContext';
import { grey } from '@mui/material/colors';
import StyleConstants from 'js/StyleConstants';
import { validateEmail } from 'js/helpers/EmailUtils';
import useEvent from 'js/hooks/useEvent';

const styles = (theme) => ({
  form: {
    height: '100%',
  },
  slide: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  textField: {
    margin: [[theme.spacing(1), 0]],
    width: '100%',
    color: grey['500'],
  },
  icon: {
    color: StyleConstants.palette.primary.main,
  },
  slideButton: {
    marginTop: theme.spacing(1),
  },
});

const RestorePasswordForm = ({ classes, shown, onSlide, onSubmit }) => {
  const LangFile = useLangFile();
  const emailRef = useRef(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const restorePasswordButtonDisabled = useMemo(() => {
    return !shown || email === '';
  }, [shown, email]);

  const handleEmailChanged = useCallback((e) => {
    let newValue = e.target.value.trim();

    setEmail(newValue);
  }, []);

  const handleOnFocus = useCallback((e) => {
    setEmailError(false);
  }, []);

  const handleOnBlur = useCallback(
    (e) => {
      setEmailError(!validateEmail(email));
    },
    [email]
  );

  const handleOnSubmit = useCallback(
    (e) => {
      onSubmit(email);
    },
    [email]
  );

  const enterHandler = (event) => {
    if (!shown) {
      return;
    }

    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault();
      handleOnSubmit();
    }
  };

  useEvent('keydown', enterHandler, emailRef.current);

  useEffect(() => {
    if (emailRef.current) {
      if (shown) {
        emailRef.current.focus();
      } else {
        emailRef.current.blur();
      }
    }
  }, [shown, emailRef.current]);

  return (
    <form className={classes.form}>
      <div className={classes.slide}>
        <div>
          <Stack direction={'column'}>
            <Typography variant={'h6'} align={'center'}>
              {LangFile.RestorePasswordForm.title}
            </Typography>

            <Grid container direction={'row'} wrap={'nowrap'}>
              <Grid
                container
                size={1}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Lock className={classes.icon} />
              </Grid>
              <Grid size={11}>
                <TextField
                  fullWidth
                  classes={{ root: classes.textField }}
                  className={'remove-autofill-color'}
                  variant={'outlined'}
                  tabIndex={shown ? 1 : -1}
                  placeholder={LangFile.RestorePasswordForm.emailHintText}
                  value={email}
                  color={'secondary'}
                  InputProps={{
                    autoComplete: 'email',
                  }}
                  inputRef={emailRef}
                  onFocus={handleOnFocus}
                  onBlur={handleOnBlur}
                  label={emailError ? LangFile.RestorePasswordForm.emailError : ''}
                  error={emailError}
                  onChange={handleEmailChanged}
                />
              </Grid>
            </Grid>

            <Grid
              container
              sx={{
                justifyContent: 'center',
              }}
            >
              {LangFile.RestorePasswordForm.description}
            </Grid>
          </Stack>
        </div>

        <div>
          <Stack direction={'column'}>
            <Button
              fullWidth
              variant={'contained'}
              tabIndex={shown ? 2 : -1}
              color={'primary'}
              disabled={restorePasswordButtonDisabled}
              onClick={handleOnSubmit}
            >
              {LangFile.RestorePasswordForm.title}
            </Button>
            <Button
              fullWidth
              className={classes.slideButton}
              tabIndex={shown ? 3 : -1}
              color={'primary'}
              onClick={onSlide}
            >
              {LangFile.RestorePasswordForm.back}
            </Button>
          </Stack>
        </div>
      </div>
    </form>
  );
};

RestorePasswordForm.propTypes = {
  shown: PropTypes.bool,
  onSlide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default withStyles(styles)(RestorePasswordForm);
